/**
 * What a score MEANS, in one line — the foundation the citizen is strongest in
 * and the one with most room to grow. (Home hero, option C · 3 Oct)
 *
 * Only foundations the engine actually scored count: an unscored one is `null`
 * (never 0) and says nothing about the person. With fewer than two scored
 * there is no "strongest vs to grow" to tell, so there is no insight. Ties go
 * to the MESH order (M, E, S, H), so the line is stable.
 *
 * @module heroes/home
 */
import { HOME_MESH_DIMENSIONS, type HomeMeshDimension, type HomeScoreInsight } from '@tonnie/types';

/** Each foundation's name — one map for every surface that names them. */
export const MESH_DIMENSION_LABEL_KEY = {
  M: 'checkup.meshInfo.M',
  E: 'checkup.meshInfo.E',
  S: 'checkup.meshInfo.S',
  H: 'checkup.meshInfo.H',
} as const satisfies Record<HomeMeshDimension, string>;

export function scoreInsight(
  mesh: Readonly<Record<HomeMeshDimension, number | null>>
): HomeScoreInsight | null {
  const scored = HOME_MESH_DIMENSIONS.flatMap((dim) => {
    const score = mesh[dim];
    return typeof score === 'number' && Number.isFinite(score) ? [{ dim, score }] : [];
  });
  if (scored.length < 2) return null;
  const strongest = scored.reduce((best, next) => (next.score > best.score ? next : best));
  const toGrow = scored.reduce((low, next) => (next.score < low.score ? next : low));
  // Every scored foundation level: nothing stands out either way.
  if (strongest.dim === toGrow.dim || strongest.score === toGrow.score) return null;
  return { strongest: strongest.dim, toGrow: toGrow.dim };
}
